// src/react/index.tsx
import React, { createContext, useContext, useEffect, useRef } from "react";
var defaultGetSrc = ({
  src,
  width,
  height,
  fit,
  format,
  params,
  optimizerEndpoint
}) => {
  const searchParams = new URLSearchParams({
    src,
    w: width.toString(),
    h: height.toString()
  });
  if (fit)
    searchParams.set("fit", fit);
  if (format)
    searchParams.set("format", format);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
  }
  return optimizerEndpoint + "?" + searchParams.toString();
};
var defaultContext = {
  breakpoints: [640, 768, 1024, 1280, 1536],
  getSrc: defaultGetSrc,
  targetFormats: ["avif", "webp"],
  optimizerEndpoint: "/img"
};
var OpenImgContext = createContext(defaultContext);
function OpenImgContextProvider({
  breakpoints,
  getSrc,
  targetFormats,
  optimizerEndpoint,
  children
}) {
  const contextValue = {
    breakpoints: breakpoints || defaultContext.breakpoints,
    getSrc: getSrc || defaultContext.getSrc,
    targetFormats: targetFormats || defaultContext.targetFormats,
    optimizerEndpoint: optimizerEndpoint || defaultContext.optimizerEndpoint
  };
  return /* @__PURE__ */ React.createElement(OpenImgContext.Provider, {
    value: contextValue
  }, children);
}
function Image({
  src,
  width,
  height,
  fit,
  isAboveFold,
  placeholder,
  params,
  ...imgProps
}) {
  const ref = useRef(null);
  useEffect(() => {
    const element = ref.current;
    if (!element || !placeholder) {
      return;
    }
    function clearStyles(element2) {
      element2.style.backgroundImage = "";
      element2.style.backgroundSize = "";
      element2.style.backgroundRepeat = "";
    }
    if (element.complete) {
      clearStyles(element);
    }
    element.addEventListener("load", () => {
      clearStyles(element);
    });
  }, [placeholder]);
  if (!src || !width || !height) {
    console.error("openimg: The `src`, `width`, and `height` props are required for the Image/Img component.");
    return null;
  }
  let widthNum = typeof width === "string" ? parseInt(width) : width;
  let heightNum = typeof height === "string" ? parseInt(height) : height;
  const ratio = widthNum / heightNum;
  const { breakpoints, getSrc, targetFormats, optimizerEndpoint } = useContext(OpenImgContext);
  const filteredBreakpoints = breakpoints.filter((bp) => bp <= widthNum);
  const fetchPriorityKey = React.version.startsWith("18") ? "fetchpriority" : "fetchPriority";
  const fetchPriorityProp = {
    [fetchPriorityKey]: isAboveFold ? "high" : "low"
  };
  return /* @__PURE__ */ React.createElement("picture", null, targetFormats.map((format) => /* @__PURE__ */ React.createElement("source", {
    key: format,
    width,
    height,
    srcSet: filteredBreakpoints.length ? [...filteredBreakpoints, widthNum].map((w) => getSrc({
      src,
      width: w,
      height: w / ratio,
      fit,
      format,
      params,
      optimizerEndpoint
    }) + ` ${w}w`).join(", ") : getSrc({
      src,
      width: widthNum,
      height: heightNum,
      fit,
      format,
      params,
      optimizerEndpoint
    }),
    sizes: filteredBreakpoints.length ? filteredBreakpoints.map((w) => `(max-width: ${w}px) ${w}px`).join(", ") + `, ${widthNum}px` : `${widthNum}px`,
    type: `image/${format}`
  })), /* @__PURE__ */ React.createElement("img", {
    ref,
    width,
    height,
    role: imgProps.alt ? undefined : "presentation",
    loading: isAboveFold ? "eager" : "lazy",
    decoding: isAboveFold ? "auto" : "async",
    ...fetchPriorityProp,
    src: getSrc({
      src,
      width: widthNum,
      height: heightNum,
      fit,
      params,
      optimizerEndpoint
    }),
    srcSet: filteredBreakpoints.length ? [...filteredBreakpoints, widthNum].map((w) => getSrc({
      src,
      width: w,
      height: w / ratio,
      fit,
      params,
      optimizerEndpoint
    }) + ` ${w}w`).join(", ") : undefined,
    sizes: filteredBreakpoints.length ? filteredBreakpoints.map((w) => `(max-width: ${w}px) ${w}px`).join(", ") + `, ${widthNum}px` : undefined,
    ...imgProps,
    style: {
      backgroundImage: placeholder ? `url(${placeholder})` : undefined,
      backgroundSize: placeholder ? "cover" : undefined,
      backgroundRepeat: "no-repeat",
      ...imgProps.style
    }
  }));
}
export {
  defaultGetSrc,
  OpenImgContextProvider,
  Image as Img,
  Image
};
